import React, { useMemo } from 'react';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Row,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  FolderOpenOutlined,
  MoreOutlined,
  PlusOutlined,
  CloudUploadOutlined,
  ForkOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import { EpcWbsLibraryCatalogueSummary } from './EpcWbsLibraryCatalogueSummary';

const { Text } = Typography;

export type PackageRow = {
  id: string;
  code: string;
  name: string;
  description?: string;
  status?: string;
  version_number?: number;
  version_label?: string;
  node_count?: number;
  cost_template_count?: number;
  estimate_plug_count?: number;
  updated_at?: string;
};

export interface EpcWbsLibraryPackageCatalogueProps {
  libraryId: string;
  packages: PackageRow[];
  loading?: boolean;
  readOnly?: boolean;
  search: string;
  readinessFilter?: string;
  sortFilter?: string;
  selectedPackageId?: string;
  onSelectPackage: (id: string) => void;
  onCreatePackage: () => void;
  onEditPackage: (pkg: PackageRow) => void;
  onSeed?: () => void;
}

export const EpcWbsLibraryPackageCatalogue: React.FC<EpcWbsLibraryPackageCatalogueProps> = ({
  libraryId,
  packages,
  loading,
  readOnly,
  search,
  readinessFilter = 'all',
  sortFilter = 'code',
  selectedPackageId,
  onSelectPackage,
  onCreatePackage,
  onEditPackage,
  onSeed,
}) => {
  const qc = useQueryClient();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = packages;
    if (q) {
      rows = rows.filter(
        (p) => p.code.toLowerCase().includes(q) || p.name.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [packages, search]);

  const readinessQ = useQuery({
    queryKey: ['epc-wbs-lib-readiness-batch', libraryId, filtered.map((p) => p.id).join(',')],
    queryFn: async () => {
      const entries = await Promise.all(
        filtered.map(async (p) => {
          try {
            const r = await epcWbsLibraryService.getPackageReadiness(String(p.id));
            return [String(p.id), r] as const;
          } catch {
            return [String(p.id), null] as const;
          }
        })
      );
      return Object.fromEntries(entries);
    },
    enabled: filtered.length > 0,
    staleTime: 30_000,
  });

  const visible = useMemo(() => {
    let rows = filtered;
    if (readinessFilter !== 'all') {
      rows = rows.filter((p) => {
        const r = readinessQ.data?.[String(p.id)];
        if (!r) return readinessFilter === 'incomplete';
        if (readinessFilter === 'ready') return r.isPlugReady;
        if (readinessFilter === 'incomplete') return !r.isPlugReady;
        return true;
      });
    }
    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sortFilter === 'usage') {
        return (Number(b.estimate_plug_count) || 0) - (Number(a.estimate_plug_count) || 0);
      }
      if (sortFilter === 'nodes') {
        return (Number(b.node_count) || 0) - (Number(a.node_count) || 0);
      }
      if (sortFilter === 'name') {
        return a.name.localeCompare(b.name);
      }
      return a.code.localeCompare(b.code);
    });
    return sorted;
  }, [filtered, readinessFilter, readinessQ.data, sortFilter]);

  const stats = useMemo(() => {
    let ready = 0;
    let incomplete = 0;
    let totalPlugs = 0;
    for (const p of packages) {
      const r = readinessQ.data?.[String(p.id)];
      if (r?.isPlugReady) ready++;
      else if (r) incomplete++;
      totalPlugs += Number(p.estimate_plug_count) || 0;
    }
    return { ready, incomplete, totalPlugs };
  }, [packages, readinessQ.data]);

  const duplicateM = useMutation({
    mutationFn: (packageId: string) => epcWbsLibraryService.duplicatePackage(packageId),
    onSuccess: () => {
      message.success('Package duplicated');
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const publishM = useMutation({
    mutationFn: (packageId: string) => epcWbsLibraryService.publishPackage(packageId),
    onSuccess: () => {
      message.success('Package published');
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-readiness'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const reviseM = useMutation({
    mutationFn: (packageId: string) => epcWbsLibraryService.revisePackage(packageId),
    onSuccess: () => {
      message.success('Draft revision created');
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteM = useMutation({
    mutationFn: (packageId: string) => epcWbsLibraryService.deletePackage(packageId),
    onSuccess: () => {
      message.success('Package archived');
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const exportM = useMutation({
    mutationFn: (packageId: string) => epcWbsLibraryService.exportPackage(packageId),
    onSuccess: (blob, packageId) => {
      const pkg = packages.find((p) => String(p.id) === packageId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pkg?.code || 'package'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const rowMenu = (row: PackageRow): MenuProps['items'] => {
    const status = String(row.status || 'Draft');
    return [
      { key: 'open', icon: <FolderOpenOutlined />, label: 'Open' },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit',
        disabled: readOnly || status === 'Published',
      },
      {
        key: 'publish',
        icon: <CloudUploadOutlined />,
        label: 'Publish',
        disabled: readOnly || status !== 'Draft',
      },
      {
        key: 'revise',
        icon: <ForkOutlined />,
        label: 'Revise',
        disabled: readOnly || status !== 'Published',
      },
      { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate', disabled: readOnly },
      { key: 'export', icon: <ExportOutlined />, label: 'Export Excel' },
      { type: 'divider' },
      { key: 'archive', icon: <DeleteOutlined />, label: 'Archive', danger: true, disabled: readOnly },
    ];
  };

  const onMenuClick = (row: PackageRow, key: string) => {
    const id = String(row.id);
    if (key === 'open') onSelectPackage(id);
    if (key === 'edit') onEditPackage(row);
    if (key === 'publish') publishM.mutate(id);
    if (key === 'revise') reviseM.mutate(id);
    if (key === 'duplicate') duplicateM.mutate(id);
    if (key === 'export') exportM.mutate(id);
    if (key === 'archive') deleteM.mutate(id);
  };

  if (!loading && packages.length === 0) {
    return (
      <Empty
        description="No packages in this catalogue yet"
        style={{ marginTop: 48 }}
      >
        {!readOnly ? (
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreatePackage}>
              Create package
            </Button>
            {onSeed ? (
              <Button onClick={onSeed}>Bootstrap sample catalogue</Button>
            ) : null}
          </Space>
        ) : null}
      </Empty>
    );
  }

  return (
    <div>
      <EpcWbsLibraryCatalogueSummary libraryId={libraryId} onSelectPackage={onSelectPackage} />
      <Space wrap style={{ marginBottom: 16 }}>
        <Tag color="blue">{packages.length} packages</Tag>
        {readinessQ.data ? (
          <>
            <Tag color="success">{stats.ready} plug-ready</Tag>
            <Tag color="warning">{stats.incomplete} need work</Tag>
          </>
        ) : null}
        {stats.totalPlugs > 0 ? <Tag>{stats.totalPlugs} estimate plugs</Tag> : null}
      </Space>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {visible.length} work package{visible.length === 1 ? '' : 's'} — click a card to open the
        WBS editor
      </Text>
      {!loading && visible.length === 0 ? (
        <Empty
          description={
            readinessFilter !== 'all'
              ? 'No packages match this readiness filter'
              : 'No packages match your search'
          }
          style={{ marginTop: 32 }}
        />
      ) : null}
      <Row gutter={[16, 16]}>
        {visible.map((row) => {
          const r = readinessQ.data?.[String(row.id)];
          const selected = selectedPackageId === String(row.id);
          return (
            <Col key={row.id} xs={24} sm={12} lg={8} xl={6}>
              <Card
                hoverable
                size="small"
                onClick={() => onSelectPackage(String(row.id))}
                onDoubleClick={() => onSelectPackage(String(row.id))}
                style={{
                  borderColor: selected ? '#91caff' : undefined,
                  background: selected ? '#f0f7ff' : undefined,
                  height: '100%',
                }}
                actions={
                  readOnly
                    ? undefined
                    : [
                        <Dropdown
                          key="more"
                          menu={{
                            items: rowMenu(row),
                            onClick: ({ key, domEvent }) => {
                              domEvent.stopPropagation();
                              onMenuClick(row, key);
                            },
                          }}
                          trigger={['click']}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Actions
                          </Button>
                        </Dropdown>,
                      ]
                }
              >
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text strong>{row.code}</Text>
                  <Text ellipsis style={{ width: '100%' }}>
                    {row.name}
                  </Text>
                  {row.description ? (
                    <Text type="secondary" ellipsis style={{ width: '100%', fontSize: 12 }}>
                      {row.description}
                    </Text>
                  ) : null}
                  <Space wrap size={4}>
                    <Tag
                      color={
                        String(row.status) === 'Published'
                          ? 'success'
                          : String(row.status) === 'Archived'
                            ? 'default'
                            : 'processing'
                      }
                    >
                      {row.status || 'Draft'}
                    </Tag>
                    <Tag>v{row.version_number || 1}</Tag>
                    <Tag>{row.node_count ?? 0} nodes</Tag>
                    <Tag>{row.cost_template_count ?? 0} CBS</Tag>
                    {(row.estimate_plug_count ?? 0) > 0 ? (
                      <Tag color="geekblue">{row.estimate_plug_count} in use</Tag>
                    ) : null}
                    {r ? (
                      <Tag color={r.isPlugReady ? 'success' : 'warning'}>
                        {r.isPlugReady ? 'Ready' : 'Incomplete'}
                      </Tag>
                    ) : (
                      <Tag>…</Tag>
                    )}
                  </Space>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};
